import React, { useState, useMemo, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import "./Input.css";
import Tooltip from '../Tooltip/Tooltip';
import { isoToMonthDayYear } from '../../store/quotesAndNotificationsSlice';

const TextInput = props => {

	const {
		label,
		labelId,
		labelClass,
		inputId,
		inputClass,
		handleChange,
		handleValidation,
		value,
		handleBlur,
		placeHolder,
		isRequired,
		disabled,
		handleKeyDown
	} = props;

	let _labelClass = labelClass ? labelClass : "text_input_label";
	let _inputClass = inputClass ? inputClass : "text_input";

	if ( handleValidation && !handleValidation( value ) ) {
		_inputClass += " invalidInput";
	}
	if ( disabled ){
		_inputClass += ' text_input_disabled';
	}

	return (
		<>
			{
				label &&
				<label
					className={ _labelClass }
					id={ labelId }
				>
					{ label }{ isRequired && <span className="required">*</span> }
				</label>
			}
			<div style={{position: 'relative'}} className={ disabled ? ' input_is_disabled' : ''}>
				<input
					type="text"
					id={ inputId }
					className={ _inputClass }
					value={ value }
					onChange={ handleChange }
					onBlur={ handleBlur }
					placeholder={ placeHolder }
					disabled={ disabled }
					onKeyDown={ handleKeyDown }
					onPaste={ (e) => e.preventDefault() }
				/>
				{
					disabled &&
					<div className="textinput_disabled_tooltip">
						<Tooltip
							size={14}
							color="#333333"
							strokeWidth={1}
							fontSize={12}
							tip="This input field is disabled and cannot be edited."
							alignment = 'right'
							tipStyles={
								{
									position: 'absolute',
									right: '-0.5rem',
									top: '210%',
									backgroundColor: '#ffffff',
									fontSize: '12px',
									zIndex: '5',
									border: '1px solid gray',
									borderRadius: '3px',
									padding: '5px',
									boxShadow: '1px 1px 2px 1px rgba(0, 0, 0, 0.3)',
									textAlign: 'left'
								}
							}
						/>
					</div>
				}
			</div>
		</>
	);
}

TextInput.propTypes = {

	handleValidation: PropTypes.func,
};

const Caret = props =>{
	const className = props.hasFocus ? "caret caret_up" : props.hasBeenToggled ? "caret caret_down" : "caret";
	return(
		<svg className={ className } version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
			 viewBox="0 0 400 400" >
			<polygon points="386.258,91.567 203.718,273.512 21.179,91.567 0,112.815 203.718,315.87 407.437,112.815 "/>
		</svg>
	);
}

const Clear = props =>{
	return (
		<svg fill="#505050" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" width="12px" height="12px">
			<path d="M 4.9902344 3.9902344 A 1.0001 1.0001 0 0 0 4.2929688 5.7070312 L 10.585938 12 L 4.2929688 18.292969 A 1.0001 1.0001 0 1 0 5.7070312 19.707031 L 12 13.414062 L 18.292969 19.707031 A 1.0001 1.0001 0 1 0 19.707031 18.292969 L 13.414062 12 L 19.707031 5.7070312 A 1.0001 1.0001 0 0 0 18.980469 3.9902344 A 1.0001 1.0001 0 0 0 18.292969 4.2929688 L 12 10.585938 L 5.7070312 4.2929688 A 1.0001 1.0001 0 0 0 4.9902344 3.9902344 z"/>
		</svg>
	);
}

const Searchable = props =>{
	
	const {
		inputId,
		inputClass,
		options,
		value,
		handleChange,
		handleSelect,
		handleClearValue,
		label,
		labelId,
		labelClass,
		isRequired,
		// isValid,
		placeHolder,
		disabled,
		handleKeyDown
	} = props;

	const [ hasFocus, setHasFocus ] = useState(false);
	const [ hasBeenToggled, setHasBeenToggled ] = useState(false);
	const inputRef = useRef(null);

	let _labelClass = labelClass ? labelClass : "text_input_label";
	let _inputClass = inputClass ? inputClass : "search_input";

	const handleContainerBlur = event =>{
		if ( !event.currentTarget.contains( event.relatedTarget ) ){
			setHasFocus(false);
		}
	};

	const handleContainerFocus = (e) =>{
		if( e.target.getAttribute('name') !== 'dropdown_button' ){
			if( !disabled ){
				if ( !hasBeenToggled ){
					setHasBeenToggled(true);
				}
				setHasFocus(true);
			}
		}
	};

	const handleDropdownButtonClick = () =>{
		if( !disabled ){
			if ( !hasBeenToggled ){
				setHasBeenToggled(true);
			}
			setHasFocus(!hasFocus);
		}
	};

	const handleClearButtonClick = () =>{
		handleClearValue?.();
		if( hasFocus ){
			inputRef.current.focus();
		}
	}

	const filteredOptions = useMemo(() =>{
		if( !value ){
			return options.sort((a, b) => a.localeCompare(b));//.sort((a, b) => a.localeCompare(b));
		}
		return options.filter(option => option.toLowerCase().includes( value.toString().toLowerCase() )).sort((a, b) => a.localeCompare(b));//.sort((a, b) => a.localeCompare(b));
	}, [ value, options ]);

	const listItems = useMemo(() =>{
		return (
			filteredOptions.map((option, i) =>{
				return(
					<li
						onClick={
							(event) => {
								handleSelect(event);
								setHasFocus(false);
							} 
						}
						className="search_option"
						// role="option"
						key={ i }
					>
						{ option }
					</li>
				);
			})
		);
	}, [ filteredOptions, handleSelect ]);

	const listClass = (hasFocus && filteredOptions.length > 0 && !(filteredOptions.length === 1 && value === filteredOptions[0]) ) ? "search_list" : "display_none";

	if( disabled ){
		_inputClass += " display_input_disabled";
	}

	//role="combobox"
	return (
		<>
			{
				label &&
				<label
					className={ _labelClass }
					id={ labelId }
				>
					{ label }{ isRequired && <span className="required">*</span> }
				</label>
			}
			<div
				className="searchable_wrapper"
				onFocus={ handleContainerFocus }
				onBlur={ handleContainerBlur }
			>
				<div className={ disabled ? 'input_is_disabled' : '' }>
					<input 
						type="text"
						id={ inputId }
						className={ _inputClass }
						onChange={ handleChange }
						// aria-autocomplete="list"
						value={ value }
						placeholder={ placeHolder }
						disabled={ disabled }
						ref={ inputRef }
						onKeyDown={ handleKeyDown }
						onPaste={ (e) => e.preventDefault() }
					/>
					{
						value && !disabled &&
						<div
							className="searchable_clear_button"
							onClick={ handleClearButtonClick }
							tabIndex="-1"
							role="button"
							name="dropdown_button"
						>
							<Clear />
						</div>
					}
					{
						!disabled ?
						<div
							className="searchable_dropdown_button"
							tabIndex="-1"
							role="button"
							// aria-label="Show product desciptions"
							//onClick={ () => setHasFocus( (disabled ? false : !hasFocus) ) }
							onClick={ handleDropdownButtonClick }
							name="dropdown_button"
						>
							<Caret hasFocus={ hasFocus } hasBeenToggled={ hasBeenToggled } />
						</div>
						:
						<div className="searchable_disabled_tooltip">
							<Tooltip
								size={14}
								color="#333333"
								strokeWidth={1}
								fontSize={12}
								tip="This input field is disabled and cannot be edited."
								alignment = 'right'
								tipStyles={
									{
										position: 'absolute',
										top: '155%',
										right: '0',
										backgroundColor: '#ffffff',
										fontSize: '12px',
										zIndex: '5',
										border: '1px solid gray',
										borderRadius: '3px',
										padding: '5px',
										boxShadow: '1px 1px 2px 1px rgba(0, 0, 0, 0.3)',
										textAlign: 'left'
									}
								}
							/>
						</div>
					}
				</div>
				<div className="search_list_container">
					<ul
						className={ listClass }
						// role="listbox"
						tabIndex="-1"
					>
						{ listItems }
					</ul>
				</div>
			</div>
		</>
	);
}

Searchable.propTypes = {
	options: PropTypes.array.isRequired,
	handleChange: PropTypes.func.isRequired,
	handleValidation: PropTypes.func,
	inputId: PropTypes.string,
	placeHolder: PropTypes.string,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired,
}

const Checkbox = props =>{

	const {
		inputId,
		label,
		handleChange,
		name,
		checkboxClass,
		labelClass,
		checked,
		value
	} = props;

	const checkbox = useRef(null);

	const _checkboxClass = checkboxClass ? checkboxClass : "checkbox";
	const _labelClass = labelClass ? labelClass : "checkbox_label";

	return (
		<div className="checkbox_component_wrapper">
			<input 
				type="checkbox"
				id={ inputId }
				className={ _checkboxClass }
				ref={ checkbox }
				onChange={ handleChange }
				name={ name }
				checked={ checked }
				value={ value }
			/>
			<label
				className={ _labelClass }
				htmlFor={ checkbox.current }
				onClick={ () => checkbox.current.click() }
			>
				{ label }
			</label>
		</div>
	);
}

const Textarea = props =>{

	const {
		label,
		labelId,
		labelClass,
		inputId,
		inputClass,
		handleChange,
		handleValidation,
		value,
		isRequired,
		disabled,
		handleKeyDown
	} = props;

	let _labelClass = labelClass ? labelClass : "text_input_label";
	let _inputClass = inputClass ? inputClass : "text_area";

	if ( handleValidation && !handleValidation( value ) ) {
		_inputClass += " invalidInput";
	}

	return (
		<>
			{
				label &&
				<label
					className={ _labelClass }
					id={ labelId }
				>
					{ label }{ isRequired && <span className="required">*</span> }
				</label>
			}
			<textarea
				id={ inputId }
				className={ _inputClass }
				onChange={ handleChange }
				disabled={ disabled }
				value={ value }
				onKeyDown={ handleKeyDown }
				onPaste={ (e) => e.preventDefault() }
			></textarea>
		</>
	);
}

const Date = props =>{

	const {
		label,
		labelId,
		isRequired,
		inputId,
		inputClass,
		value,
		handleChange,
		labelClass,
		disabled,
		min,
		max,
		handleBlur
	} = props;

	let _labelClass = labelClass ? labelClass : "date_input_label";
	let _inputClass = inputClass ? inputClass : "date_input";

	const dateRef = useRef(null);
	const monthTextRef = useRef(null);
	const dayTextRef = useRef(null);
	const yearTextRef = useRef(null);

	const [previousDateVal, setPreviousDateVal] = useState("");
	const [dateMonth, setDateMonth] = useState("");
	const [dateDay, setDateDay] = useState("");
	const [dateYear, setDateYear] = useState("");
	const [preserveValues, setPreserveValues] = useState(false);

	useEffect(() => {
		if( value ){
			const dateStringArr = value.split('-');
			setDateYear( dateStringArr[0] );
			setDateMonth( dateStringArr[1] );
			setDateDay( dateStringArr[2] );
			setPreviousDateVal( value );
		} else if( previousDateVal !== "" ){
			setPreviousDateVal("");
			if( !preserveValues ){
				setDateYear("");
				setDateMonth("");
				setDateDay("");
			} 
			setPreserveValues(false);
		}
	}, [value])

	function handleDateTextChange(val, field){
		let value = val;
		if( value.match(/^[0-9]+$/g) ){ //ensure only numbers
			if( field === 'month' ){
				if( value[0] > '1' ){	//highest month is 12
					value = '0' + value[0];
				}
				if( value === '00' ){	//lowest month is 01
					value = '01';
				}
				if( value.length === 2 ){
					let dayVal = dateDay;
					if( dayVal ){ //check that the day is valid
						dayVal = getValidDay(value, dayVal, dateYear);
					}
					if( dayVal !== '' && dateYear !== '' ){
						const { isValid, rangeViolation } = isInMinMaxRange(min, max, value, dayVal, dateYear);
						if( isValid ){
							if( value.length === 2 && dayVal.length === 2 && dateYear.length === 4 ){
								handleChange(dateYear +"-"+ value +"-"+ dayVal);
							}
						} else if( rangeViolation === 'min' ){
							handleChange( min );
							value = min.split('-')[1];
						} else {
							handleChange( max );
							value = max.split('-')[1];
						}
					}
					dayTextRef.current.focus();
				}
				setDateMonth( value );
			} else if( field === 'day' ){
				if( value[0] > '3' ){	//highest day is 31
					value = '0' + value[0];
				}
				if( value === '00' ){	//lowest day is 01
					value = '01';
				}
				if( value.length === 2 ){
					let monthVal = dateMonth;
					if( monthVal ){
						value = getValidDay(monthVal, value, dateYear);
					}
					if( monthVal !== '' && dateYear !== '' ){
						const { isValid, rangeViolation } = isInMinMaxRange(min, max, monthVal, value, dateYear);
						if( isValid ){
							if( value.length === 2 && monthVal.length === 2 && dateYear.length === 4 ){
								handleChange(dateYear +"-"+ monthVal +"-"+ value);
							}
						} else if( rangeViolation === 'min' ){
							handleChange( min );
							value = min.split('-')[2];
						} else {
							handleChange( max );
							value = max.split('-')[2];
						}
					}
					yearTextRef.current.focus();
				}
				setDateDay( value );
			} else if( field === 'year' ){
				if( value[0] === '0' ){
					value = value.split('');
					value[0] = '1';
					value = value.join('');
				} else if( value[0] > '2'){
					value = value.split('');
					value[0] = '2';
					value = value.join('');
				}
				if( value.length === 4 ){
					const { isValid, rangeViolation } = isInMinMaxRange(min, max, dateMonth, dateDay, value);
					if( isValid ){
						if( dateMonth !== '' && dateDay !== '' && dateMonth.length === 2 && dateDay.length === 2 ){
							let d_val = getValidDay(dateMonth, dateDay, value);
							handleChange(value +"-"+ dateMonth +"-"+ d_val);
						}
					} else if( dateMonth.length === 2 && dateDay.length === 2 ) {
						if( rangeViolation === 'min' ){
							handleChange( min );
							value = min.split('-')[0];
						} else {
							handleChange( max );
							value = max.split('-')[0];
						}
					} else {
						if( rangeViolation === 'min' ){
							value = min.split('-')[0];	
						} else {
							value = max.split('-')[0];
						}
					}
				}
				setDateYear( value );
			}
		} else if( value === '' ){
			if( field === 'year' ){
				setDateYear( value );
			} else if( field === 'month' ){
				setDateMonth( value );
			} else if( field === 'day' ){
				setDateDay( value );
			}
			setPreserveValues(true);
			handleChange('');
		}
	}

	return (
		<div style={{position: 'relative', width: '100%'}}> 
			{
				label &&
				<label
					className={ _labelClass }
					id={ labelId }
				>
					{ label }{ isRequired && <span className="required">*</span> }
				</label>
			}
			<div className="date_inputs_container">
				<input
					type="date"
					id={ inputId }
					ref={ dateRef }
					className={ _inputClass }
					value={ value }
					onChange={ (e) => handleChange(e.target.value) }
					disabled={ disabled }
					min={ min }
					max={ max }
					//onBlur={ handleBlur }
					onClick={ () => monthTextRef.current.focus() }
				/>
				<div className="date_text_input_container">
					<input
						type="text"
						className="date_text_input date_text_month"
						ref={ monthTextRef }
						value={ dateMonth }
						disabled={ disabled }
						onChange={ (e) => handleDateTextChange(e.target.value, 'month') }
						onPaste={ (e) => e.preventDefault() }
						maxLength="2"
						placeholder="mm"
						onKeyDown={
							(e)=>{
								if( e.code === 'ArrowRight' && e.target.maxLength === e.target.selectionStart ){
									dayTextRef.current.focus();
								}
							}
						}
						onBlur={ 
							(e) => {
								if( e.target.value.length === 1 ){
									handleDateTextChange('0'+e.target.value, 'month');
								}
							}
						}
					/>
					<span className="date_slash">/</span>
					<input
						type="text"
						className="date_text_input date_text_day"
						ref={ dayTextRef }
						value={ dateDay }
						disabled={ disabled }
						onChange={ (e) => handleDateTextChange(e.target.value, 'day') }
						onPaste={ (e) => e.preventDefault() }
						maxLength="2"
						placeholder="dd"
						onKeyDown={
							(e)=>{
								if( e.code === 'ArrowRight' && e.target.maxLength === e.target.selectionStart ){
									yearTextRef.current.focus();
								} else if( e.code === 'ArrowLeft' && e.target.selectionStart === 0 ){
									monthTextRef.current.focus();
								}
							}
						}
						onBlur={ 
							(e) => {
								if( e.target.value.length === 1 ){
									handleDateTextChange('0'+e.target.value, 'day');
								}
							}
						}
					/>
					<span className="date_slash">/</span>
					<input
						type="text"
						className="date_text_input date_text_year"
						ref={ yearTextRef }
						value={ dateYear }
						disabled={ disabled }
						onChange={ (e) => handleDateTextChange(e.target.value, 'year') }
						onPaste={ (e) => e.preventDefault() }
						maxLength="4"
						placeholder="yyyy"
						onKeyDown={
							(e)=>{
								if( e.code === 'ArrowLeft' && e.target.selectionStart === 0 ){
									dayTextRef.current.focus();
								}
							}
						}
						onBlur={ 
							(e) => {
								if( e.target.value.length === 1 ){
									handleDateTextChange('200'+e.target.value, 'year');
								} else if( e.target.value.length === 2 ){
									handleDateTextChange('20'+e.target.value, 'year');
								} else if( e.target.value.length === 3 ){
									handleDateTextChange('2'+e.target.value, 'year');
								}
							}
						}
					/>
				</div>
			</div>
			{
				disabled &&
				<div className="dateinput_disabled_tooltip">
					<Tooltip
						size={14}
						color="#333333"
						strokeWidth={1}
						fontSize={12}
						tip="This input field is disabled and cannot be edited."
						alignment = 'right'
						tipStyles={
							{
								position: 'absolute',
								right: '-0.5rem',
								top: '200%',
								backgroundColor: '#ffffff',
								fontSize: '12px',
								zIndex: '100',
								border: '1px solid gray',
								borderRadius: '3px',
								padding: '5px',
								boxShadow: '1px 1px 2px 1px rgba(0, 0, 0, 0.3)',
								textAlign: 'left'
							}
						}
					/>
				</div>
			}
		</div>
	);
}

function isInMinMaxRange(min, max, month, day, year){
	let inRange = true;
	let rangeViolation = '';
	const minDate = min.split('-');
	const maxDate = max.split('-');
	if( year ){
		if( year > maxDate[0] || year < minDate[0] ){ //see if year is in range
			inRange = false;
			if( year > maxDate[0] ){
				rangeViolation = 'max';
			} else {
				rangeViolation = 'min';
			}
		} else if( year === maxDate[0] ){
			if( month && month > maxDate[1] ){
				inRange = false;
				rangeViolation = 'max';
			} else if( month === maxDate[1] ){
				if( day && day > maxDate[2] ){
					inRange = false;
					rangeViolation = 'max';
				}
			}
		} else if( year === minDate[0] ){
			if( month && month < minDate[1] ){
				inRange = false;
				rangeViolation = 'min';
			} else if( month === minDate[1] ){
				if( day && day < minDate[2] ){
					inRange = false;
					rangeViolation = 'min';
				}
			}
		}
	}
	return {isValid: inRange, rangeViolation: rangeViolation};
}

function isLeapYear(year){
	return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
}

function getValidDay(month, day, year){
	let validDay;
	if( month && day[0] <= '3' ){ //month exists, no must limit days based on month
		let leapYear = true;
		if( year && year.length === 4 ){ //year isn't empty and is a full year
			leapYear = isLeapYear(year); //check if leap year for february
		}
		let highestPossibleDay = getHighestPossibleDay(month, leapYear);
		validDay = day > highestPossibleDay ? highestPossibleDay : day;
	} else {
		if( day[0] > '3' ){ //highest day value begin with 3
			validDay = '0' + day[0];
		} else if( day <= '31' ){ //highest possible day is 31
			validDay = day;
		} else {
			validDay = '31';
		}
	}
	return validDay;
}

function getHighestPossibleDay(month, isLeapYear){
	let highestDay;
	switch( month ){
		case '02':
			highestDay = isLeapYear ? '29' : '28';
		break;
		case '04':
			highestDay = '30';
		break;
		case '06':
			highestDay = '30';
		break;
		case '09':
			highestDay = '30';
		break;
		case '11':
			highestDay = '30';
		break;
		default:
			highestDay = '31';
	}
	return highestDay;
}

const File = props => {
	const {
		label,
		labelId,
		// isRequired,
		inputId,
		//inputClass,
		handleChange,
		handleClear,
		labelClass
	} = props;

	let _labelClass = labelClass ? labelClass : "upload_label";
	const [ hasFile, setHasFile ] = useState(false);
	const ref = useRef(null);
	const labelRef = useRef(null);

	function clearFileUpload(){
		handleClear?.( inputId );
		ref.current.value = null;
		labelRef.current.innerHTML = "Choose File";
		setHasFile(false);
	}

	function handleFileSelection(e){
		handleChange?.( e );
		labelRef.current.innerHTML = ref.current.files[0]['name'];
		setHasFile(true);
	}

	return (
		<div className="file_upload_container individual_files">
			<label className={_labelClass} id={labelId}>{label}</label>
			<div className="upload_flex_container">
				<div className="file_name_container">
					<p className="display_file_name" ref={ labelRef }></p>
					{
						hasFile &&
						<span 
							onClick={ clearFileUpload }
							className="clear_file_upload"
						>x</span>
					}
				</div>
				<div className="file_upload">
					<span className="choose_file_btn">Choose File</span>
					<input 
						onChange={ (e) => handleFileSelection(e) }
						className="upload" 
						type="file"
						id={ inputId }
						ref={ ref }
					/>
				</div>
			</div>
		</div>
	);
}

const Display = props =>{
	const {
		label,
		labelId,
		isRequired,
		inputId,
		inputClass,
		value,
		labelClass,
		disabled
	} = props;

	let _labelClass = labelClass ? labelClass : "text_input_label";
	let _inputClass = inputClass ? inputClass : "display_input";

	if( disabled ){
		_inputClass += " display_input_disabled";
	}

	return (
		<>
			{
				label &&
				<label
					className={ _labelClass }
					id={ labelId }
				>
					{ label }{ isRequired && <span className="required">*</span> }
				</label>
			}
			<input
				id={ inputId }
				className={ _inputClass }
				disabled="disabled"
				value={value}
			/>
		</>
	);
}

const Input = props =>{
	switch( props.type ){
		case "text":

			return <TextInput {...props} />;

		case "searchable":

			return <Searchable {...props} />;

		case "checkbox":

			return <Checkbox {...props} />;

		case "textarea":

			return <Textarea {...props} />;

		case "date":

			return <Date {...props} />;

		case "display":

			return <Display {...props} />;

		case "file":
			return <File {...props} />

		default:

			return <TextInput {...props} />;
	}
}

function handleValidationPropType(props, propName, componentName){

	if (  props[propName] ){
		if ( typeof(props[propName]) !== 'function' ){
			return new Error(
				'Invalid prop `'+ propName +'` of type '+
				typeof(props[propName]) +' supplied to `'+ componentName +'`, expected `function`.'
			);
		}

		if ( !props['name'] ){
			return new Error(
				'prop handleValidation must be provided with a name prop corresponding to the key of the ReGex value'
			);
		}
	}
}

function handleChangePropType(props, propName, componentName){

	if( props.type !== 'display' ){

		if ( !props[propName] ){
			return new Error(
				'prop '+ propName +' is required for any mutable Input type.'
			);
		}

		if ( typeof(props[propName]) !== 'function' ){
			return new Error(
				'Invalid prop `'+ propName +'` of type '+
				typeof(props[propName]) +' supplied to `'+ componentName +'`, expected `function`.'
			);
		}
	}
}

Input.propTypes = {
	type: PropTypes.string.isRequired,
	handleChange: handleChangePropType,
	handleBlur: PropTypes.func,
	handleValidation: handleValidationPropType,
	placeHolder: PropTypes.string,
	isValid: PropTypes.bool,
	inputId: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	name: PropTypes.string,
	options: PropTypes.array
};

export default Input;